<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import SourcePopin from '~/components/ui/SourcePopin.vue'
import { isDestructiveConfirmPhraseValid } from '~/utils/destructiveConfirm'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'

const SourceInput = defineAsyncWithSkeleton(
  function loadSourceInput() {
    return import('~/components/ui/SourceInput.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

type Props = {
  open: boolean
  title: string
  description?: string
  confirmPhrase: string
  confirmText?: string
  cancelText?: string
  loading?: boolean
  descriptionId?: string
}

const props = withDefaults(defineProps<Props>(), {
  description: '',
  confirmText: 'Supprimer',
  cancelText: 'Annuler',
  loading: false,
  descriptionId: 'destructive-confirm-desc',
})

const emit = defineEmits<{
  (event: 'update:open', value: boolean): void
  (event: 'confirm'): void
}>()

const typedPhrase = ref('')

const isOpen = computed({
  get() {
    return props.open
  },
  set(value: boolean) {
    emit('update:open', value)
  },
})

const confirmInputLabel = computed(() => {
  return `Pour confirmer, saisissez « ${props.confirmPhrase} »`
})

const canConfirm = computed(() => {
  return isDestructiveConfirmPhraseValid(typedPhrase.value, props.confirmPhrase) && !props.loading
})

watch(
  () => props.open,
  function resetTypedPhraseOnClose(isDialogOpen) {
    if (!isDialogOpen) {
      typedPhrase.value = ''
    }
  }
)

function onConfirmClick() {
  if (!canConfirm.value) {
    return
  }
  emit('confirm')
}

function onClose() {
  if (props.loading) return
  isOpen.value = false
}
</script>

<template>
  <SourcePopin
    v-model:open="isOpen"
    :title="title"
    size="medium"
    :has-clear="false"
    :close-on-backdrop="false"
    :close-on-escape="!loading"
    :description-id="descriptionId"
    @close="onClose"
  >
    <template #description>
      <slot name="description">
        <template v-if="description">
          {{ description }}
        </template>
        <span v-if="confirmPhrase">
          Pour confirmer, saisissez <strong>{{ confirmPhrase }}</strong> ci-dessous.
        </span>
      </slot>
    </template>

    <SourceInput
      v-if="confirmPhrase"
      :model-value="typedPhrase"
      :label="confirmInputLabel"
      placeholder=""
      size="medium"
      :disabled="loading"
      @update:model-value="(value: string) => (typedPhrase = value)"
    />

    <template #actions>
      <SourceButton
        size="small"
        :text="cancelText"
        type="deleteSecondary"
        :disabled="loading"
        @click="onClose"
      />
      <SourceButton
        size="small"
        icon="delete"
        :text="confirmText"
        type="deletePrimary"
        :disabled="!canConfirm"
        @click="onConfirmClick"
      />
    </template>
  </SourcePopin>
</template>
